import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  ORIGINAL_MEDIA_HREF_ATTR,
  ORIGINAL_MEDIA_SRC_ATTR,
  RESOLVED_MEDIA_ATTR,
  extractAppMediaPath,
  normalizeMediaBeforeSave,
} from '../richTextMediaHelpers';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { setResolved } from '../../media/mediaResolverSlice';
import { mediaProbeApi } from '../../media/api/mediaProbeApi';
import { store } from '../../../app/store';

type UseResolvedMediaSyncParams = {
  editor: Editor | null;
};

type UseResolvedMediaSyncResult = {
  normalizeContentForPersisting: (content: string) => string;
  scheduleResolvedMediaSync: (immediate?: boolean) => Promise<void>;
};

const sharedInFlight = new Map<string, Promise<string | null>>();

export const useResolvedMediaSync = ({
  editor,
}: UseResolvedMediaSyncParams): UseResolvedMediaSyncResult => {
  const resolvedUrlCacheRef = React.useRef<Map<string, string>>(new Map());
  const pendingResolvePromiseRef = React.useRef<Promise<void> | null>(null);
  const pendingResolveTimerRef = React.useRef<number | null>(null);
  const pendingResolvePromiseResolveRef = React.useRef<(() => void) | null>(null);

  const dispatch = useAppDispatch();
  const resolvedEntries = useAppSelector((state) => state.mediaResolver?.resolved ?? {});

  const DEFAULT_TTL_SECONDS = Number(import.meta.env.VITE_MEDIA_PRESIGNED_TTL_SECONDS) || 14 * 60;

  const resolveSingleMediaPath = React.useCallback(
    async (path: string): Promise<string | null> => {
      if (resolvedUrlCacheRef.current.has(path)) {
        return resolvedUrlCacheRef.current.get(path) || null;
      }

      const storeEntry = resolvedEntries[path];
      if (storeEntry && storeEntry.expiresAt > Date.now()) {
        resolvedUrlCacheRef.current.set(path, storeEntry.url);
        return storeEntry.url;
      }

      if (sharedInFlight.has(path)) {
        const sharedUrl = await sharedInFlight.get(path);
        resolvedUrlCacheRef.current.set(path, sharedUrl || path);
        return sharedUrl || null;
      }

      const resolvePromise = (async () => {
        try {
          try {
            const probe = await dispatch(
              mediaProbeApi.endpoints.probePath.initiate(path, { subscribe: false })
            ).unwrap();

            const resolvedUrl = probe?.resolvedUrl;

            if (!resolvedUrl) {
              return null;
            }

            dispatch(setResolved({ path, url: resolvedUrl, ttlSeconds: DEFAULT_TTL_SECONDS }));
            return resolvedUrl;
          } catch (_err) {
            return null;
          }
        } catch (_error) {
          return null;
        } finally {
          sharedInFlight.delete(path);
        }
      })();

      sharedInFlight.set(path, resolvePromise);
      const finalUrl = await resolvePromise;

      resolvedUrlCacheRef.current.set(path, finalUrl || path);

      return finalUrl;
    },
    [dispatch, resolvedEntries, DEFAULT_TTL_SECONDS]
  );

  const resolveMediaPaths = React.useCallback(
    async (paths: string[]): Promise<void> => {
      const uniquePaths = Array.from(
        new Set(
          paths
            .map((path) => extractAppMediaPath(path ?? ''))
            .filter((path): path is string => !!path)
        )
      ).filter((path) => !resolvedUrlCacheRef.current.has(path));

      if (!uniquePaths.length) return;

      try {
        await Promise.all(
          uniquePaths.map(async (path) => {
            try {
              await resolveSingleMediaPath(path);
            } catch (err) {
              console.warn('RichTextEditor: failed to GET media path', { path, err });
            }
          })
        );
      } catch (error) {
        console.warn('RichTextEditor: failed to resolve media URLs', { paths: uniquePaths, error });
      }
    },
    [resolveSingleMediaPath]
  );

  const normalizeContentForPersisting = React.useCallback((content: string): string => {
    let normalizedContent = normalizeMediaBeforeSave(content);

    resolvedUrlCacheRef.current.forEach((resolvedUrl, originalPath) => {
      if (!resolvedUrl || !originalPath || resolvedUrl === originalPath) return;
      normalizedContent = normalizedContent.split(resolvedUrl).join(originalPath);
    });

    return normalizeMediaBeforeSave(normalizedContent);
  }, []);

  const applyResolvedMediaToEditor = React.useCallback(async () => {
    if (!editor || editor.isDestroyed) return;

    const pathsToResolve = new Set<string>();

    editor.state.doc.descendants((node) => {
      if (node.type.name === 'image' && node.attrs.src) {
        let originalPath = node.attrs[ORIGINAL_MEDIA_SRC_ATTR] || node.attrs.src;
        resolvedUrlCacheRef.current.forEach((resolved, original) => {
          if (resolved === node.attrs.src) originalPath = original;
        });
        const extractedPath = extractAppMediaPath(originalPath);
        if (extractedPath) pathsToResolve.add(extractedPath);
      }

      node.marks?.forEach((mark) => {
        if (mark.type.name !== 'link' || !mark.attrs.href) return;

        let originalPath = mark.attrs[ORIGINAL_MEDIA_HREF_ATTR] || mark.attrs.href;
        resolvedUrlCacheRef.current.forEach((resolved, original) => {
          if (resolved === mark.attrs.href) originalPath = original;
        });

        const extractedPath = extractAppMediaPath(originalPath);
        if (extractedPath) pathsToResolve.add(extractedPath);
      });
    });

    if (pathsToResolve.size === 0) return;

    await resolveMediaPaths(Array.from(pathsToResolve));

    if (editor.isDestroyed) return;

    const { tr } = editor.state;
    let appliedModifications = false;

    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === 'image' && node.attrs.src) {
        let originalPath = node.attrs[ORIGINAL_MEDIA_SRC_ATTR] || node.attrs.src;
        resolvedUrlCacheRef.current.forEach((resolved, original) => {
          if (resolved === node.attrs.src) originalPath = original;
        });

        const extractedPath = extractAppMediaPath(originalPath);
        if (extractedPath) {
          const resolvedUrl = resolvedUrlCacheRef.current.get(extractedPath);
          if (resolvedUrl && resolvedUrl !== extractedPath && node.attrs.src !== resolvedUrl) {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              src: resolvedUrl,
              [ORIGINAL_MEDIA_SRC_ATTR]: extractedPath,
              [RESOLVED_MEDIA_ATTR]: 'true',
            });
            appliedModifications = true;
          }
        }
      }

      node.marks?.forEach((mark) => {
        if (mark.type.name !== 'link' || !mark.attrs.href) return;

        let originalPath = mark.attrs[ORIGINAL_MEDIA_HREF_ATTR] || mark.attrs.href;
        resolvedUrlCacheRef.current.forEach((resolved, original) => {
          if (resolved === mark.attrs.href) originalPath = original;
        });

        const extractedPath = extractAppMediaPath(originalPath);
        if (!extractedPath) return;

        const resolvedUrl = resolvedUrlCacheRef.current.get(extractedPath);
        if (!resolvedUrl || resolvedUrl === extractedPath || mark.attrs.href === resolvedUrl)
          return;

        const from = pos;
        const to = pos + node.nodeSize;
        tr.removeMark(from, to, mark.type);
        tr.addMark(
          from,
          to,
          mark.type.create({
            ...mark.attrs,
            href: resolvedUrl,
            [ORIGINAL_MEDIA_HREF_ATTR]: extractedPath,
            [RESOLVED_MEDIA_ATTR]: 'true',
          })
        );
        appliedModifications = true;
      });
    });

    if (appliedModifications) {
      editor.view.dispatch(tr.setMeta('addToHistory', false));
    }
  }, [editor, resolveMediaPaths]);

  const scheduleResolvedMediaSync = React.useCallback(
    (immediate = false) => {
      if (immediate) {
        // Cancel any pending timer
        if (pendingResolveTimerRef.current) {
          clearTimeout(pendingResolveTimerRef.current);
          pendingResolveTimerRef.current = null;
        }

        if (!pendingResolvePromiseRef.current) {
          pendingResolvePromiseRef.current = new Promise<void>((resolve) => {
            pendingResolvePromiseResolveRef.current = resolve;
          });
        }

        void (async () => {
          try {
            await applyResolvedMediaToEditor();
          } finally {
            const resolve = pendingResolvePromiseResolveRef.current;
            pendingResolvePromiseResolveRef.current = null;
            pendingResolvePromiseRef.current = null;
            resolve?.();
          }
        })();

        return pendingResolvePromiseRef.current as Promise<void>;
      }

      if (pendingResolvePromiseRef.current) {
        return pendingResolvePromiseRef.current;
      }

      pendingResolvePromiseRef.current = new Promise<void>((resolve) => {
        pendingResolvePromiseResolveRef.current = resolve;
      });

      pendingResolveTimerRef.current = window.setTimeout(() => {
        pendingResolveTimerRef.current = null;
        void (async () => {
          try {
            await applyResolvedMediaToEditor();
          } finally {
            const resolve = pendingResolvePromiseResolveRef.current;
            pendingResolvePromiseResolveRef.current = null;
            pendingResolvePromiseRef.current = null;
            resolve?.();
          }
        })();
      }, 120);

      return pendingResolvePromiseRef.current;
    },
    [applyResolvedMediaToEditor]
  );

  React.useEffect(() => {
    return () => {
      if (pendingResolveTimerRef.current) {
        clearTimeout(pendingResolveTimerRef.current);
        pendingResolveTimerRef.current = null;
      }

      const resolve = pendingResolvePromiseResolveRef.current;
      if (resolve) {
        pendingResolvePromiseResolveRef.current = null;
        pendingResolvePromiseRef.current = null;
        try {
          resolve();
        } catch (e) {
          console.warn('useResolvedMediaSync: error on unmount', { e });
        }
      }
    };
  }, []);

  return {
    normalizeContentForPersisting,
    scheduleResolvedMediaSync,
  };
};

export type ResolvedPath = { path: string; url: string | null };

export const resolveMediaPathsStandalone = async (paths: string[]): Promise<ResolvedPath[]> => {
  const DEFAULT_TTL_SECONDS = Number(import.meta.env.VITE_MEDIA_PRESIGNED_TTL_SECONDS) || 14 * 60;

  return Promise.all(
    paths.map(async (path) => {
      try {
        const cachedEntry = store.getState().mediaResolver?.resolved[path];
        if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
          return { path, url: cachedEntry.url };
        }

        if (sharedInFlight.has(path)) {
          const sharedPromise = sharedInFlight.get(path);
          const sharedUrl = sharedPromise ? await sharedPromise : null;
          return { path, url: sharedUrl ?? null };
        }

        const resolvePromise = (async () => {
          try {
            try {
              const probe = await store
                .dispatch(mediaProbeApi.endpoints.probePath.initiate(path, { subscribe: false }))
                .unwrap();
              const resolvedUrl = probe?.resolvedUrl ?? null;
              if (resolvedUrl) {
                try {
                  store.dispatch(
                    setResolved({ path, url: resolvedUrl, ttlSeconds: DEFAULT_TTL_SECONDS })
                  );
                } catch (_) {
                  // ignore errors when writing cache
                }
              }
              return resolvedUrl;
            } catch (_err) {
              return null;
            }
          } catch (_e) {
            return null;
          } finally {
            sharedInFlight.delete(path);
          }
        })();

        sharedInFlight.set(path, resolvePromise);
        const finalUrl = (await resolvePromise) ?? null;
        return { path, url: finalUrl };
      } catch (_e) {
        return { path, url: null };
      }
    })
  );
};
